import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase, isSupabaseConfigured } from './supabaseClient';

const AuthContext = createContext(null);

/**
 * Auth provider — wraps the app with user/session/role state.
 * If Supabase is not configured, the app runs in "local mode" (no auth).
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLocalMode] = useState(!isSupabaseConfigured());

  // Load session on mount
  useEffect(() => {
    if (isLocalMode) {
      setLoading(false);
      return;
    }

    // Get current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user || null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [isLocalMode]);

  const fetchProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      setProfile(data);
    } catch (err) {
      console.error('Error fetching profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const signUp = useCallback(async (email, password, displayName) => {
    if (isLocalMode) return { error: { message: 'Supabase not configured' } };

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { display_name: displayName },
        emailRedirectTo: `${window.location.origin}/login`,
      },
    });
    return { data, error };
  }, [isLocalMode]);

  const signIn = useCallback(async (email, password) => {
    if (isLocalMode) return { error: { message: 'Supabase not configured' } };

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  }, [isLocalMode]);

  const signOut = useCallback(async () => {
    if (isLocalMode) return;
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
  }, [isLocalMode]);

  const updateProfile = useCallback(async (updates) => {
    if (isLocalMode || !user) return;

    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id)
      .select()
      .single();

    if (!error && data) {
      setProfile(data);
    }
    return { data, error };
  }, [isLocalMode, user]);

  const value = {
    user,
    profile,
    loading,
    isLocalMode,
    isAuthenticated: !!user,
    isAdmin: profile?.role === 'admin',
    displayName: profile?.display_name || user?.email?.split('@')[0] || 'User',
    signUp,
    signIn,
    signOut,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
